type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

const SCRIPT_ID = 'google-recaptcha-v3';
const defaultAction = 'contact_form_submit';
const configuredSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() ?? '';
const configuredAction = import.meta.env.VITE_RECAPTCHA_ACTION?.trim() || defaultAction;

let clientPromise: Promise<Grecaptcha> | null = null;

const awaitClientReady = (client: Grecaptcha): Promise<Grecaptcha> =>
  new Promise((resolve, reject) => {
    try {
      client.ready(() => resolve(client));
    } catch (error) {
      reject(error);
    }
  });

const injectScript = (siteKey: string): Promise<void> => {
  const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript) {
    if (window.grecaptcha) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script.')), {
        once: true
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'));
    document.head.appendChild(script);
  });
};

const loadClient = async (): Promise<Grecaptcha> => {
  if (!configuredSiteKey) {
    throw new Error('reCAPTCHA site key is missing.');
  }

  if (typeof window === 'undefined') {
    throw new Error('reCAPTCHA can only run in a browser environment.');
  }

  if (window.grecaptcha) {
    return awaitClientReady(window.grecaptcha);
  }

  if (!clientPromise) {
    clientPromise = injectScript(configuredSiteKey)
      .then(() => {
        if (!window.grecaptcha) {
          throw new Error('reCAPTCHA client failed to initialize.');
        }

        return awaitClientReady(window.grecaptcha);
      })
      .catch((error) => {
        clientPromise = null;
        throw error;
      });
  }

  return clientPromise;
};

export const isRecaptchaConfigured = (): boolean => Boolean(configuredSiteKey);

export const getRecaptchaAction = (): string => configuredAction;

export const requestRecaptchaToken = async (action = configuredAction): Promise<string> => {
  const client = await loadClient();
  const token = await client.execute(configuredSiteKey, { action });
  const normalizedToken = token.trim();

  if (!normalizedToken) {
    throw new Error('reCAPTCHA returned an empty token.');
  }

  return normalizedToken;
};
